import React, { useCallback, useRef } from 'react';
import { LTButton, LTText } from '@lambdatestincprivate/lt-components';
import { DownloadIcon, LinkExternalIcon, LockIcon, MarkGithubIcon } from '@primer/octicons-react';
import { GH_OAUTH_CLIENT_ID } from '../../config';
import { useWindowFileDrop } from '../hooks/useWindowFileDrop';
import styles from './states.module.css';

/**
 * The Connect-GitHub gateway (store phase 'gh-auth'): a gh-artifact link
 * needs a GitHub identity, which the user grants ONCE per device. The
 * screen leads with the one-time connect; the download-and-drop fallback
 * rides below (promoted when the user just declined the consent).
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function GhConnectGate(_ref) {
  var ghAuth = _ref.ghAuth,
    onConnect = _ref.onConnect,
    onFile = _ref.onFile;
  var ref = ghAuth.ref,
    reason = ghAuth.reason;
  var inputRef = useRef(null);
  var dragging = useWindowFileDrop(onFile);
  var canConnect = GH_OAUTH_CLIENT_ID !== '';
  var onPick = useCallback(function (e) {
    var _e$target$files;
    var file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
    if (file) onFile(file);
    e.target.value = '';
  }, [onFile]);
  var openOnGitHub = useCallback(function () {
    window.open("https://github.com/".concat(encodeURIComponent(ref.owner), "/").concat(encodeURIComponent(ref.repo), "/actions"), '_blank', 'noopener');
  }, [ref.owner, ref.repo]);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.wrap,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.ghCard,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.ghTile,
        "aria-hidden": "true",
        children: /*#__PURE__*/_jsx(MarkGithubIcon, {
          size: 24
        })
      }), /*#__PURE__*/_jsx(LTText, {
        as: "h1",
        text: "Connect GitHub to open this run",
        color: "var(--lt-text-primary)",
        className: styles.heading,
        style: {
          fontSize: '20px',
          lineHeight: '28px',
          fontWeight: 600
        }
      }), /*#__PURE__*/_jsx(LTText, {
        as: "p",
        text: "This run\u2019s evidence is stored as a GitHub Actions artifact, and GitHub only shares artifacts with signed-in accounts.",
        variant: LTText.TextVariant.SUBHEADER_REGULAR,
        color: "var(--lt-text-secondary)",
        className: styles.body
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.ghSource,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.ghSourceIcon,
          "aria-hidden": "true",
          children: /*#__PURE__*/_jsx(MarkGithubIcon, {
            size: 16
          })
        }), /*#__PURE__*/_jsxs("span", {
          className: styles.ghSourceText,
          children: [/*#__PURE__*/_jsx(LTText, {
            as: "span",
            text: "".concat(ref.owner, "/").concat(ref.repo),
            variant: LTText.TextVariant.SUBHEADER_BOLD,
            color: "var(--lt-text-primary)",
            className: styles.ghSourceRepo
          }), /*#__PURE__*/_jsx(LTText, {
            as: "span",
            text: "Artifact ".concat(ref.artifactId, " \xB7 GitHub Actions"),
            variant: LTText.TextVariant.SMALL_REGULAR,
            color: "var(--lt-text-muted)",
            className: styles.ghSourceMeta
          })]
        })]
      }), reason === 'token-rejected' && /*#__PURE__*/_jsx(LTText, {
        as: "p",
        text: "Your GitHub session expired \u2014 connect again.",
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "var(--lt-text-muted)",
        className: styles.ghNote
      }), reason === 'declined' && canConnect && /*#__PURE__*/_jsx(LTText, {
        as: "p",
        text: "You declined the GitHub sign-in \u2014 try again, or download the file below.",
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "var(--lt-text-muted)",
        className: styles.ghNote
      }), canConnect ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.ghConnectBtn,
          children: /*#__PURE__*/_jsx(LTButton, {
            label: "Connect GitHub",
            variant: LTButton.Variant.PRIMARY,
            size: LTButton.Size.MEDIUM,
            leadingIcon: /*#__PURE__*/_jsx(MarkGithubIcon, {
              size: 16,
              "aria-hidden": "true"
            }),
            onClick: onConnect
          })
        }), /*#__PURE__*/_jsxs("span", {
          className: styles.ghAccess,
          children: [/*#__PURE__*/_jsx(LockIcon, {
            size: 14,
            "aria-hidden": "true"
          }), /*#__PURE__*/_jsx(LTText, {
            as: "span",
            text: "One-time, read-only access \u2014 your token stays in this browser",
            variant: LTText.TextVariant.SMALL_REGULAR,
            color: "var(--lt-text-muted)"
          })]
        })]
      }) : /*#__PURE__*/_jsx(LTText, {
        as: "p",
        text: "GitHub sign-in isn\u2019t configured on this deployment \u2014 use the download option below.",
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "var(--lt-text-muted)",
        className: styles.ghNote
      }), /*#__PURE__*/_jsx("div", {
        className: styles.ghDivider,
        children: /*#__PURE__*/_jsx(LTText, {
          as: "span",
          text: "or",
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--lt-text-muted)"
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.ghAlt,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.ghAltIcon,
          "aria-hidden": "true",
          children: /*#__PURE__*/_jsx(DownloadIcon, {
            size: 20
          })
        }), /*#__PURE__*/_jsxs("span", {
          className: styles.ghAltText,
          children: [/*#__PURE__*/_jsx(LTText, {
            as: "span",
            text: "Download the file instead",
            variant: LTText.TextVariant.SUBHEADER_BOLD,
            color: "var(--lt-text-primary)"
          }), /*#__PURE__*/_jsx(LTText, {
            as: "span",
            text: "No connection needed \u2014 save it from GitHub, then drop it here.",
            variant: LTText.TextVariant.SMALL_REGULAR,
            color: "var(--lt-text-muted)"
          })]
        }), /*#__PURE__*/_jsx("span", {
          className: styles.ghAltAction,
          children: /*#__PURE__*/_jsx(LTButton, {
            label: "Download",
            variant: reason === 'declined' && canConnect ? LTButton.Variant.PRIMARY : LTButton.Variant.DEFAULT,
            size: LTButton.Size.SMALL,
            trailingIcon: /*#__PURE__*/_jsx(LinkExternalIcon, {
              size: 14,
              "aria-hidden": "true"
            }),
            onClick: openOnGitHub
          })
        })]
      }), /*#__PURE__*/_jsx(LTButton, {
        label: "Choose downloaded file",
        variant: LTButton.Variant.INVISIBLE,
        size: LTButton.Size.SMALL,
        onClick: function onClick() {
          var _inputRef$current;
          return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.click();
        }
      }), /*#__PURE__*/_jsx("input", {
        ref: inputRef,
        type: "file",
        accept: ".evidence,.zip,application/zip",
        className: "ev-sr-only",
        "aria-label": "Choose the downloaded artifact zip or .evidence file",
        onChange: onPick
      })]
    }), dragging && /*#__PURE__*/_jsx("div", {
      className: styles.ghDragOverlay,
      "aria-hidden": "true",
      children: /*#__PURE__*/_jsx("span", {
        className: "ev-t-header20",
        children: "Drop to open"
      })
    })]
  });
}